import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import WireDistanceWarning from 'components/Wire/DistanceWarning'
import { deleteWireAction } from 'actions/entityActions'
import { updateCurrentEntityAction } from 'actions/applicationActions'
import { changeCameraAction } from 'actions/cameraActions'
import { zoomToSingleCameraAction } from 'actions/mapActions'
import { CAMERA_SELECTED_RGB_COLORS } from 'helpers/globals'
import getWireDistance from 'helpers/wires/getWireDistance'
import getTypeIconString from 'helpers/wiredDevices/getTypeIconString'

const mapStateToProps = ({ ui: { wiringPanelOpen, isImperial }, map: { ref } }) => {
  return {
    map: ref,
    wiringPanelOpen,
    isImperial
  }
}

const iconPath = 'm 2 .6 c -1 0 -1.3 0.7 -0.9 1.5 l 1.4 2.4 c 0.5 0.8 1.7 1.5 2.6 1.5 h 2.1 c 1 0 2.1 -0.7 2.6 -1.5 l 1.4 -2.4 c 0.5 -0.8 0.1 -1.5 -0.9 -1.5 l -8.3 0 l 0 0 z m -0.9 23 c 0 1 0.8 1.7 1.7 1.7 h 6.8 c 1 0 1.7 -0.8 1.7 -1.7 v -14.4 c 0 -1 -0.8 -1.7 -1.7 -1.7 h -6.8 c -1 0 -1.7 0.8 -1.7 1.7 v 14.4 l 0 0 z'

class Diagram extends React.Component {
  constructor(props) {
    super(props)
    this.animationFrame = null
  }

  drawCanvas = _ => {
    if (!this.props.wire) return

    const { strokeColor, end: { type } } = this.props.wire
    const canvas = this.refs.connectionCanvas
    const dimensions = canvas.getBoundingClientRect()
    const width = Math.round(dimensions.width)
    const height = Math.round(dimensions.height)

    // handle scale and clear
    const ratio = window.devicePixelRatio
    canvas.width = width * ratio
    canvas.height = height * ratio

    const ctx = canvas.getContext('2d')
    ctx.scale(ratio, ratio)
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    ctx.beginPath()
    ctx.moveTo(5, 5)
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.setLineDash(type === 'doorController' ? [0, 7.5] : [6, 9])
    ctx.strokeStyle = strokeColor
    ctx.lineTo(width / 2, 5)
    ctx.lineTo(width / 2, height - 5)
    ctx.lineTo(width - 5, height - 5)
    ctx.stroke()

    this.animationFrame = null
  }

  handleResize = _ => {
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame)
    this.animationFrame = requestAnimationFrame(this.drawCanvas)
  }

  componentDidMount = _ => {
    window.addEventListener('resize', this.handleResize)
    this.drawCanvas()
  }

  componentWillUnmount = _ => window.removeEventListener('resize', this.handleResize)

  componentDidUpdate = prevProps => {
    const { wire, startDevice, wiringPanelOpen } = this.props

    if (wire && !prevProps.wire || startDevice.color !== prevProps.startDevice.color || wiringPanelOpen && !prevProps.wiringPanelOpen) this.handleResize()
  }

  cameraListener = camera => {
    const { wiringPanel, updateCurrentEntityAction, changeCameraAction, zoomToSingleCameraAction } = this.props

    if (wiringPanel) return

    return _ => {
      camera.active ? updateCurrentEntityAction({ id: camera.id, type: 'camera' }) : changeCameraAction(camera.id)
      zoomToSingleCameraAction(camera)
    }
  }

  otherListener = (type, device) => {
    const { wiringPanel, updateCurrentEntityAction, map } = this.props

    if (type !== 'networkCloset' && wiringPanel) return

    return _ => {
      updateCurrentEntityAction({ id: device.id, type })
      map.panTo(device.location)
    }
  }

  deleteWireListener = _ => {
    const { startDevice, wire: { id }, endDevice, deleteWireAction } = this.props

    if (startDevice.locked) {
      if (!window.confirm(`${startDevice.name} is locked, are you sure you want to delete this cable?`)) return
    } else if (endDevice.locked) {
      if (!window.confirm(`${endDevice.name} is locked, are you sure you want to delete this cable?`)) return
    }

    deleteWireAction(id)
  }

  deviceRow = (device, type) => {
    const { cameraListener, otherListener } = this

    return (
      <a
        onClick={type === 'camera' ? cameraListener(device) : otherListener(type, device)}
        style={{ fontSize: this.props.mobile ? '1.4rem' : 'inherit' }}
      >
        {device.name} <FontAwesomeIcon icon='arrow-right' />
      </a>
    )
  }

  cameraIcon = _ => {
    const { cameraListener, props: { startDevice, wiringPanel, classes } } = this

    return (
      <svg
        viewBox='0 0 12.4 26'
        onClick={cameraListener(startDevice)}
        className={classes.cameraIcon + (wiringPanel ? ' skipCursor' : '')}
      >
        <path d={iconPath} stroke={`rgba(${CAMERA_SELECTED_RGB_COLORS[startDevice.color]}, 0.80)`} fill='none' strokeWidth='2' />
      </svg>
    )
  }

  getIconString = (device, type) => {
    switch (type) {
      case 'wiredDevice':
        return getTypeIconString(device.type)
      case 'doorController':
        return ['far', 'microchip']
      case 'door':
        return ['far', 'door-open']
    }
  }

  otherIcon = (start = true) => {
    const { otherListener, getIconString, props: { startDeviceType, startDevice, wire, endDevice, wiringPanel, classes } } = this
    const device = start ? startDevice : endDevice
    const deviceType = start ? startDeviceType : wire.end.type

    return (
      <div
        onClick={otherListener(deviceType, device)}
        className={start ? (classes.otherIcon + (wiringPanel ? ' skipCursor' : '')) : classes.endIcon}
        style={{ background: device.color }}
      >
        <FontAwesomeIcon
          icon={getIconString(device, deviceType)}
          style={{ margin: 'auto' }}
        />
      </div>
    )
  }

  networkClosetIcon = _ => {
    const { otherListener, props: { endDevice, classes } } = this

    return (
      <FontAwesomeIcon
        icon='server'
        onClick={otherListener('networkCloset', endDevice)}
        className={classes.networkClosetIcon}
        style={{ color: endDevice.color }}
      />
    )
  }

  renderDiagram = _ => {
    const { cameraIcon, otherIcon, networkClosetIcon, otherListener, props: { wire, startDeviceType, endDevice, wiringInfo, mobile, classes } } = this

    return (
      <div className={mobile ? classes.mobileCanvasWrapper : classes.canvasWrapper}>
        { startDeviceType === 'camera' ? cameraIcon() : otherIcon() }
        { wire
          ? <canvas ref='connectionCanvas' className={classes.canvas} />
          : <div style={{ margin: 'auto' }}>{ wiringInfo }</div>
        }
        { endDevice && (wire.end.type === 'networkCloset' ? networkClosetIcon() : otherIcon(false)) }
      </div>
    )
  }

  renderHeader = _ => {
    const { wiringPanel, wire, mobile, classes } = this.props

    return (
      <div className={classes.header}>
        <span>Connection:</span>
        { wiringPanel && wire &&
          <a
            onClick={this.deleteWireListener}
            className={mobile ? classes.mobileDisconnect : classes.disconnect}
          >
            <FontAwesomeIcon icon='times-circle' />
            Disconnect
          </a>
        }
      </div>
    )
  }

  renderFooter = _ => {
    const { wire, dragging, endDevice, isImperial, classes } = this.props

    if (!wire || !endDevice) return <div className={classes.endDeviceLabel} />

    const { distance, showWarning } = getWireDistance(wire.overlay, isImperial, endDevice.dropLength)

    return (
      <div className={classes.endDeviceLabel}>
        <span className={classes.distanceText}>
          Length:
          <span className={dragging ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>
            { ` ${distance}` }
            { wire.end.type === 'networkCloset' && showWarning && <WireDistanceWarning isImperial={isImperial} /> }
          </span>
        </span>
        { this.deviceRow(endDevice, wire.end.type) }
      </div>
    )
  }

  render() {
    const { renderHeader, renderDiagram, renderFooter, deviceRow, props: { wire, startDevice, startDeviceType, wiringPanel, classes } } = this

    return (
      <div className={classes.container} style={{ padding: wiringPanel ? 0 : '.8rem' }}>
        { renderHeader() }
        { !wiringPanel && <div className={classes.cameraLabel}>{deviceRow(startDevice, startDeviceType)}</div> }
        { renderDiagram() }
        { renderFooter() }
      </div>
    )
  }
}

export default connect(mapStateToProps, { deleteWireAction, updateCurrentEntityAction, changeCameraAction, zoomToSingleCameraAction })(injectSheet(style)(Diagram))



// WEBPACK FOOTER //
// ./src/components/WireDetails/Diagram/index.js